import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type CollectionResponse, type ResourceResponse } from '@shared/api/client';
import type { Factura, FacturaFormData, FacturaListFilters } from '../types/factura';

const BASE = '/billing/invoices';

export const facturaKeys = {
  all: ['facturas'] as const,
  list: (filters?: FacturaListFilters) =>
    [...facturaKeys.all, 'list', filters ?? {}] as const,
  detail: (id: number) => [...facturaKeys.all, 'detail', id] as const,
};

export function useFacturas(filters?: FacturaListFilters) {
  return useQuery({
    queryKey: facturaKeys.list(filters),
    queryFn: async () => {
      const { data } = await api.get<CollectionResponse<Factura>>(BASE, {
        params: filters,
      });
      return data.data;
    },
  });
}

export function useFactura(id: number) {
  return useQuery({
    queryKey: facturaKeys.detail(id),
    queryFn: async () => {
      const { data } = await api.get<ResourceResponse<Factura>>(`${BASE}/${id}`);
      return data.data;
    },
    enabled: id > 0,
  });
}

export function useCreateFactura() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: FacturaFormData) => {
      const { data } = await api.post<ResourceResponse<Factura>>(BASE, payload);
      return data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: facturaKeys.all }),
  });
}

export function useUpdateFactura() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, payload }: { id: number; payload: Partial<FacturaFormData> }) => {
      const { data } = await api.put<ResourceResponse<Factura>>(`${BASE}/${id}`, payload);
      return data.data;
    },
    onSuccess: (_d, v) => {
      qc.invalidateQueries({ queryKey: facturaKeys.all });
      qc.invalidateQueries({ queryKey: facturaKeys.detail(v.id) });
    },
  });
}

export function useDeleteFactura() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => api.delete(`${BASE}/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: facturaKeys.all }),
  });
}

export function useMarkFacturaPaid() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const { data } = await api.post<ResourceResponse<Factura>>(`${BASE}/${id}/pay`);
      return data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: facturaKeys.all }),
  });
}

export function useVoidFactura() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const { data } = await api.post<ResourceResponse<Factura>>(`${BASE}/${id}/void`);
      return data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: facturaKeys.all }),
  });
}

export function useMakeRecurring() {
  return useMutation({
    mutationFn: async ({ id, frecuencia }: { id: number; frecuencia: "mensual" | "trimestral" | "anual" }) => {
      const { data } = await api.post(`${BASE}/${id}/make-recurring`, { frecuencia });
      return data;
    },
  });
}

export function useRectifyFactura() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, motivo }: { id: number; motivo?: string }) => {
      const { data } = await api.post<ResourceResponse<Factura>>(`${BASE}/${id}/rectify`, { motivo });
      return data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: facturaKeys.all }),
  });
}

export function useSetFacturaDueDate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, vencimiento }: { id: number; vencimiento: string | null }) => {
      const { data } = await api.patch<ResourceResponse<Factura>>(`${BASE}/${id}/due-date`, {
        vencimiento,
      });
      return data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: facturaKeys.all }),
  });
}

export function useResendVerifacti() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const { data } = await api.post<{ ok: boolean; message?: string | null; data: Factura }>(
        `${BASE}/${id}/verifacti-resend`,
      );
      return data;
    },
    onSuccess: (_d, id) => {
      qc.invalidateQueries({ queryKey: facturaKeys.all });
      qc.invalidateQueries({ queryKey: facturaKeys.detail(id) });
    },
  });
}

/**
 * Envía la factura por email al cliente (con el PDF adjunto).
 * El backend usa el email del cliente de la factura.
 */
export function useSendFacturaEmail() {
  return useMutation({
    mutationFn: async (id: number) => {
      const { data } = await api.post<{ ok: boolean; email: string }>(
        `${BASE}/${id}/send-email`,
      );
      return data;
    },
  });
}

/**
 * Descarga el PDF de la factura como blob (via Sanctum) y lo abre
 * en una pestaña nueva. Evita problemas de cookies cross-port que
 * tendría un <a href> directo.
 */
export async function openFacturaPdf(facturaId: number): Promise<void> {
  const response = await api.get(`${BASE}/${facturaId}/pdf`, {
    responseType: "blob",
  });
  const url = URL.createObjectURL(response.data as Blob);
  window.open(url, "_blank");
  // Liberar la URL tras un rato para que el navegador haya cargado el PDF
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/**
 * Previsualiza una factura sin guardarla en BD. Recibe el payload del
 * formulario y devuelve un PDF temporal abierto en pestaña nueva.
 */
export async function previewFacturaPdf(payload: FacturaFormData): Promise<void> {
  const response = await api.post(`${BASE}/pdf-preview`, payload, {
    responseType: "blob",
  });
  const url = URL.createObjectURL(response.data as Blob);
  window.open(url, "_blank");
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
